import clsx from 'clsx'
import first from 'lodash/first'
import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import React, { FC, useCallback, useMemo } from 'react'

import { ampli } from '@Analytics/ampli'
import SvgBin from '@Assets/SVG/Bin'
import { CartV2Type } from '@Domains/Cart/Feature/CartV2/CartV2'
import { LineItem } from '@Domains/Cart/Feature/CartV2/Components/CartOverview/LineItem/LineItem'
import { useRemoveFromCartMutation } from '@Generated/graphql'
import { useTranslation } from '@Globals/Translations'
import { Spinner } from '@Shared/Components/Spinner/Spinner'

import styles from './CartOverview.module.scss'

export interface CartOverviewProps {
  cart?: CartV2Type | null
  className?: string
  setMutatedCart: () => void
  v2?: boolean
}

export const GetNumberOfLineItemsInCart = lineItems => (lineItems || []).reduce(
  (sum, lineItem) => {
    if (lineItem.type?.__typename === 'CartLineItemTypeGiftcard') {
      return sum + lineItem.quantity
    }
    return ((!!lineItem.type && 'parent' in lineItem.type && lineItem.type.parent === null) ? (sum + lineItem.quantity) : sum)
  },
  0,
)

export const CartOverviewV2: FC<CartOverviewProps> = ({
  cart,
  className,
  setMutatedCart,
  v2,
}) => {
  const lineItems = useMemo(() => cart?.lineItems, [cart?.lineItems])
  const { t } = useTranslation()
  const [removeLineItems, { loading }] = useRemoveFromCartMutation({
    context: {
      domainProxy: true,
      userSpecific: true,
    },
  })
  const getFreeGiftParent = useCallback(lineItem => lineItems?.find(
    l => l.type && lineItem.type && l.type.__typename === 'CartLineItemTypeFreeProduct' && 'parent' in lineItem.type && l.type.link === lineItem.type.parent,
  ), [lineItems])

  return (
    <div className={`${className} ${styles.cartOverview}`}>
      {map(lineItems, lineItem => (cart?.lineItemGroups?.find(group => group.lineItemIDs?.includes(lineItem.id))
        ? null
        : (
          <LineItem
            key={lineItem.id}
            isPartOfBundle={!!(lineItem.type && 'parent' in lineItem.type && !isEmpty(lineItem.type.parent))}
            lineItem={lineItem}
            parent={getFreeGiftParent(lineItem)}
            personalizationsLinkedToLineItem={lineItems?.filter(
              l => l.type?.__typename === 'CartLineItemTypeUpsellPersonalization' && l.type?.parent === lineItem.type?.link,
            )}
            setMutatedCart={setMutatedCart}
            showPersonalizeHeader={false}
            showPersonalizeImagesOnAddedProducts={false}
            showPersonalizeImagesOnUnaddedProducts={false}
            showQuantityButtons
            v2={v2}
          />
        )
      ))}
      {map(cart?.lineItemGroups, group => (
        <div key={`lineitemgroup_${group.id}`} className={clsx(v2 ? styles.lineItemGroupWrapperV2 : styles.lineItemGroupWrapper, loading && styles.loading)}>
          {loading && <Spinner className={styles.spinner} />}
          <div className={styles.groupHeader}>
            <div className={styles.title}>{t('cart:BUNDLE_DISCOUNT_LABEL')}</div>
            <div className={styles.prices}>
              {group.totalPrice?.discount?.format && <span className={clsx(styles.discount)}>{group.totalPrice?.discount?.format}</span>}
              <span className={clsx(styles.price, group.totalPrice?.discount?.format && styles.discounted)}>{group.totalPrice?.format}</span>
            </div>
          </div>
          <div className={styles.lineItemGroup}>
            {map(cart?.lineItems?.filter(lineitem => group.lineItemIDs?.includes(lineitem.id)), (lineItem, index) => (
              <LineItem
                key={`lineitemgroup_item_${lineItem.id}`}
                bundleItemNumber={index + 1}
                className={styles.lineItemInGroup}
                groupItem
                isPartOfBundle={!!(lineItem.type && 'parent' in lineItem.type && !isEmpty(lineItem.type.parent))}
                lineItem={lineItem}
                parent={getFreeGiftParent(lineItem)}
                personalizationsLinkedToLineItem={lineItems?.filter(
                  l => l.type?.__typename === 'CartLineItemTypeUpsellPersonalization' && l.type?.parent === lineItem.type?.link,
                )}
                setMutatedCart={setMutatedCart}
                showAdditionProducts={false}
                showPersonalizationModifiers={false}
                showPersonalizeHeader={false}
                showPersonalizeImagesOnAddedProducts={false}
                showPersonalizeImagesOnUnaddedProducts={false}
                showQuantity={false}
                showQuantityBubble={false}
                showQuantityButtons={false}
                showUpsell={false}
                v2={v2}
              />
            ))}
          </div>

          <div className={styles.groupFooter}>
            {v2
              ? (
                <button
                  onClick={() => {
                    const firstLineItemID = first(group.lineItemIDs)
                    if (firstLineItemID) {
                      removeLineItems({
                        variables: {
                          lineItemID: firstLineItemID,
                        },
                      }).then((a) => {
                        ampli.cartRemoveRes({
                          is_success: isEmpty(a.errors),
                        })
                      })
                    }
                  }}
                  type="button"
                >
                  {t('cart:REMOVE_DISCOUNT_BUNDLE')}
                </button>
              )
              : (
                <>
                  <p>{t('cart:REMOVE_DISCOUNT_BUNDLE')}</p>
                  <button
                    onClick={() => {
                      const firstLineItemID = first(group.lineItemIDs)
                      if (firstLineItemID) {
                        removeLineItems({
                          variables: {
                            lineItemID: firstLineItemID,
                          },
                        }).then((a) => {
                          ampli.cartRemoveRes({
                            is_success: isEmpty(a.errors),
                          })
                        })
                      }
                    }}
                    type="button"
                  >
                    {' '}
                    <SvgBin />
                  </button>
                </>
              )}
          </div>
        </div>
      ))}

    </div>
  )
}

